import { GatewayEntreprise, GatewayPaiement } from "@conformite/gateway";
import {
  FieldValues,
  UseFormReturn,
} from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import {
  Form,
  FormField,
  FormRow,
  displayErrorToast,
  useDialog,
} from "@endya-conformite/ui-kit";
import { ID_FORM_ADHESION } from "@src/adhesion/ParcoursAdhesion.definition";
import {
  CreationEntrepriseParams,
  RenouvellementEntrepriseParams,
} from "@src/api/adhesion2.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";

import { storeEntrepriseCreationRequest } from "@src/payment/utils/createEntreprises";
import { EntreprisePaymentSepaConfirmDialog } from "@src/societes/form/EntreprisePayment/EntreprisePaymentSepaConfirmDialog";
import { PaymentElement } from "@stripe/react-stripe-js";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AdhesionVirementPage } from "./AdhesionVirementPage";
import { useStripePaiement } from "./useStripePaiement";

type AdhesionPaiementStripeFormProps<TValue extends FieldValues> = {
  formContent: ReactNode;
  formConfig: UseFormReturn<TValue, unknown, undefined>;
  modePrelevementSelectionne: GatewayPaiement.PaymentMethod;
  typeAdhesion: GatewayEntreprise.TypeAdhesionActive;
  entrepriseCreationParams:
    CreationEntrepriseParams | RenouvellementEntrepriseParams;
  isRenouvellement?: boolean;
  setIsPaiementSubmitting: (isSubmitting: boolean) => void;
};

export function AdhesionPaiementStripeForm<TValue extends FieldValues>({
  formContent,
  formConfig,
  modePrelevementSelectionne,
  typeAdhesion,
  entrepriseCreationParams,
  isRenouvellement,
  setIsPaiementSubmitting,
}: AdhesionPaiementStripeFormProps<TValue>) {
  const { onSubmit: submitStripe } = useStripePaiement(
    typeAdhesion,
    entrepriseCreationParams,
    isRenouvellement
  );
  const navigate = useNavigate();
  const { openDialog } = useDialog();

  const handleSubmit = async (values: TValue) => {
    if (modePrelevementSelectionne === GatewayPaiement.PaymentMethod.SEPA) {
      const response = await openDialog(({ dialogRef, onClose }) => (
        <EntreprisePaymentSepaConfirmDialog
          dialogRef={dialogRef}
          onClose={onClose}
        />
      ));
      if (!response) {
        return null;
      }
    }
    setIsPaiementSubmitting(true);
    if (modePrelevementSelectionne === GatewayPaiement.PaymentMethod.TRANSFER) {
      try {
        storeEntrepriseCreationRequest(typeAdhesion, entrepriseCreationParams);
        if (isRenouvellement) {
          navigate(`/renouvellement/maj-entreprises`);
        } else {
          navigate(`/adhesion/creation-entreprises`);
        }
      } catch (error) {
        handleGatewayError({
          onUnhandled: () => {
            console.error(error);
            displayErrorToast(
              "Une erreur est survenue lors de la création des entreprises"
            );
          },
        })(error);
      }
    } else {
      try {
        await submitStripe();
      } catch (error) {
        handleGatewayError({
          onUnhandled: () => {
            console.error(error);
            displayErrorToast(
              "Une erreur est survenue lors du paiement de la cotisation"
            );
          },
        })(error);
      }
    }
    setIsPaiementSubmitting(false);
    return values;
  };

  return (
    <Form formConfig={formConfig} onSubmit={handleSubmit} id={ID_FORM_ADHESION}>
      {formContent}
      {modePrelevementSelectionne ===
        GatewayPaiement.PaymentMethod.TRANSFER && <AdhesionVirementPage />}
      {modePrelevementSelectionne !==
        GatewayPaiement.PaymentMethod.TRANSFER && (
        <FormRow>
          <FormField>
            <PaymentElement id="payment-element" />
          </FormField>
        </FormRow>
      )}
    </Form>
  );
}
